import { createFileRoute } from '@tanstack/react-router';
import { format, isSameDay, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import type { DayButton } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { AppointmentCard } from '@/components/appointments/appointment-card';
import { AppointmentSkeleton } from '@/components/appointments/appointment-skeleton';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { useAppointments } from '@/hooks/use-appointments';
import { useUsers } from '@/hooks/use-users';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/calendar')({
  component: () => (
    <ProtectedRoute>
      <CalendarPage />
    </ProtectedRoute>
  ),
});

function CalendarPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(new Date());

  const { data: appointmentsData, isLoading: isLoadingAppointments, error: appointmentsError } = useAppointments();
  const { data: usersData } = useUsers();

  const appointments = appointmentsData?.appointments || [];
  const users = usersData?.users || {};

  const selectedDateAppointments = useMemo(() => {
    if (!date || !appointments) return [];

    return appointments
      .filter((appointment) => {
        const appointmentDate = new Date(appointment.startTime);
        return isSameDay(appointmentDate, date);
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [appointments, date]);

  const appointmentCounts = useMemo(() => {
    if (!appointments) return {};

    const counts: Record<string, number> = {};
    appointments.forEach((appointment) => {
      const dateKey = startOfDay(new Date(appointment.startTime)).toISOString();
      counts[dateKey] = (counts[dateKey] || 0) + 1;
    });
    return counts;
  }, [appointments]);

  const CustomDayButton = ({ day, modifiers, ...props }: ComponentProps<typeof DayButton>) => {
    const dayKey = startOfDay(day.date).toISOString();
    const appointmentCount = appointmentCounts[dayKey];

    return (
      <Button
        variant="ghost"
        size="icon"
        data-day={day.date.toLocaleDateString()}
        data-selected-single={
          modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
        }
        className={cn(
          'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground relative aspect-square size-auto w-full min-w-8 flex-col gap-1 leading-none font-normal',
          modifiers.selected && 'bg-primary text-primary-foreground',
          modifiers.today && 'bg-accent text-accent-foreground',
          modifiers.outside && 'text-muted-foreground',
        )}
        {...props}
      >
        <span>{day.date.getDate()}</span>
        {appointmentCount && appointmentCount > 0 && (
          <div className="absolute -top-1 -right-1">
            <div className="bg-blue-500 text-white text-xs rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[10px] leading-none">
              {appointmentCount}
            </div>
          </div>
        )}
      </Button>
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">{t('nav.calendar')}</h1>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('calendar.calendarView')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              className="rounded-md border"
              components={{
                DayButton: CustomDayButton,
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t('calendar.appointmentsFor')} {date ? format(date, 'MMMM d, yyyy') : t('calendar.selectedDate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {appointmentsError && (
              <div className="p-4 text-red-600 bg-red-50 rounded-md">
                Error loading appointments: {appointmentsError.message}
              </div>
            )}

            {isLoadingAppointments ? (
              <AppointmentSkeleton variant="compact" />
            ) : selectedDateAppointments.length === 0 ? (
              <EmptyState
                icon={CalendarIcon}
                title={t('calendar.noAppointments')}
                description={t('appointments.noAppointmentsScheduledForDate')}
                className="border-0 shadow-none"
              />
            ) : (
              <div className="space-y-3">
                {selectedDateAppointments.map((appointment) => (
                  <AppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    variant="compact"
                    users={users}
                    showCreatedBy={false}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
